import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { SkiPlan, SiteElement, Path } from "../types";
import {
    Save,
    X,
    Upload,
    Info,
    Download,
    Maximize,
    Minimize,
    Undo2,
    Redo2,
    Copy,
    FileJson,
    Globe,
    Lock,
    Eye,
    User,
} from "lucide-react";
import { useHistory } from "../hooks/useHistory";
import { ElementToolbar } from "./ElementToolbar";
import { ElementProperties } from "./ElementProperties";
import { PathProperties } from "./PathProperties";
import { SiteCanvas } from "./SiteCanvas";
import { DrawingToolbar } from "./DrawingToolbar";
import { CustomElementDialog } from "./CustomElementDialog";
import { PDFPreviewDialog } from "./PDFPreviewDialog";
import { exportToPDF, generatePreview } from "../utils/pdfExport";
import { toPng } from "html-to-image";
import { compressImage } from "../utils/imageCompression";
import type { SiteCanvasHandle } from "./SiteCanvas";
import * as pocketbaseService from "../utils/pocketbaseService";

interface SkiPlanEditorProps {
    plan: SkiPlan;
    // ✅ onSave = sauvegarde LOCALE (localStorage) + mise à jour state App
    onSave: (plan: SkiPlan) => void | Promise<void>;
    onCancel: () => void;
    isCreating: boolean;
    currentUserId?: string;
}

type ToolMode = "element" | "draw";

/**
 * ========= Local image store (IndexedDB)
 * - stocke les gros base64 hors localStorage
 * - clé = plan.id
 */
const LocalImageStore = (() => {
    const DB_NAME = "ski_plan_editor";
    const DB_VERSION = 1;
    const STORE = "images";

    function openDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const req = indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(STORE)) {
                    db.createObjectStore(STORE);
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }

    async function get(key: string): Promise<string | null> {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, "readonly");
            const store = tx.objectStore(STORE);
            const req = store.get(key);
            req.onsuccess = () => resolve((req.result as string) ?? null);
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => db.close();
        });
    }

    async function set(key: string, value: string): Promise<void> {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, "readwrite");
            const store = tx.objectStore(STORE);
            const req = store.put(value, key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => db.close();
        });
    }

    async function del(key: string): Promise<void> {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, "readwrite");
            const store = tx.objectStore(STORE);
            const req = store.delete(key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => db.close();
        });
    }

    return {
        get,
        set,
        del,
    };
})();

function normalizePlan(p: SkiPlan): SkiPlan {
    return {
        ...p,
        siteImage: p.siteImage ?? null,
        elements: Array.isArray(p.elements) ? p.elements : [],
        paths: Array.isArray(p.paths) ? p.paths : [],
        collaborators: Array.isArray(p.collaborators) ? p.collaborators : [],
        isPublic: !!p.isPublic,
        isReadOnly: !!p.isReadOnly,
        thumbnail: p.thumbnail ?? null,
    };
}

function safeUUID() {
    const c: any = globalThis.crypto;
    if (c?.randomUUID) return c.randomUUID();
    return `cid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function getClientId() {
    const key = "pbClientId";
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = safeUUID();
    sessionStorage.setItem(key, id);
    return id;
}

export function SkiPlanEditor({ plan, onSave, onCancel, isCreating, currentUserId }: SkiPlanEditorProps) {
    const siteCanvasRef = useRef<SiteCanvasHandle>(null);

    // ✅ Historique stable + état central
    const normalizedPlan = useMemo(() => normalizePlan(plan), [plan]);
    const history = useHistory<SkiPlan>(normalizedPlan);
    const formData = history.state;

    const cloudId = formData.cloudId;
    const isCloud = !!cloudId;

    // ===== Droits =====
    // Source de vérité : formData (pas les states) car c’est ce qui vient du cloud/reload
    const isPlanPublic = !!formData.isPublic;
    const isPlanReadOnlyMode = !!formData.isReadOnly;
    // ✅ Collab/presence UNIQUEMENT si public ET pas read-only
    const collabEnabled = isCloud && !!cloudId && isPlanPublic && !isPlanReadOnlyMode;

    const isLoggedIn = !!currentUserId;
    const isLocalDraft = !formData.cloudId;

    const isOwner = !!currentUserId && (!formData.userId || formData.userId === currentUserId);

    const isCollaborator =
        !!formData.cloudId &&
        !!currentUserId &&
        (formData.collaborators ?? []).includes(currentUserId);

    const canEdit = isLocalDraft || isOwner || isCollaborator;
    const isReadOnly = !canEdit;

    // ===== UI state =====
    const [toolMode, setToolMode] = useState<ToolMode>("element");
    const [selectedElement, setSelectedElement] = useState<SiteElement | null>(null);
    const [selectedPath, setSelectedPath] = useState<Path | null>(null);
    const [activeElementType, setActiveElementType] = useState<SiteElement["type"] | null>(null);
    const [activePathType, setActivePathType] = useState<Path["type"] | null>(null);
    const [drawingPath, setDrawingPath] = useState<Path | null>(null);
    const [showCustomDialog, setShowCustomDialog] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showPDFPreview, setShowPDFPreview] = useState(false);
    const [previewImage, setPreviewImage] = useState<string>("");

    const [isPublic, setIsPublic] = useState(!!normalizedPlan.isPublic);
    const [isReadOnlyMode, setIsReadOnlyMode] = useState(!!normalizedPlan.isReadOnly);
    const [isPropertiesPanelMinimized, setIsPropertiesPanelMinimized] = useState(true);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // ✅ Image en RAM (base64) ou URL
    const [siteImageDraft, setSiteImageDraft] = useState<string | null>(normalizedPlan.siteImage ?? null);

    // ✅ l'image “réelle” à afficher partout
    const effectiveImage = siteImageDraft ?? formData.siteImage ?? null;

    // ✅ si tu en as besoin pour PDFPreviewDialog
    const planForCanvas = useMemo(() => {
        return {
            ...formData,
            siteImage: effectiveImage,
        };
    }, [formData, effectiveImage]);

    // ===== Helpers UI =====
    const waitNextPaint = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
    const nextPaint = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
    const waitUiUpdate = async () => {
        await nextPaint();
        await nextPaint();
    };

    // ===== ClientId (par onglet) =====
    const clientIdRef = useRef<string>("");
    if (!clientIdRef.current) clientIdRef.current = getClientId();

    // ===== Presence =====
    const sessionIdRef = useRef<string | null>(null);
    const [activeUsers, setActiveUsers] = useState<{ userId: string; userName: string; mode: string }[]>([]);

    // ===== Live update banner =====
    const [hasRemoteUpdate, setHasRemoteUpdate] = useState(false);
    const [remoteMeta, setRemoteMeta] = useState<{ lastEditedByName?: string; lastEditedAt?: string } | null>(null);

    const localUpdatedRef = useRef<string | null>(null);
    const ignoreRemoteUntilRef = useRef<number>(0);
    const lastLocalSaveAtRef = useRef<number>(0);

    const ackLocalSave = useCallback((updatedFromServer?: string) => {
        lastLocalSaveAtRef.current = Date.now();
        if (updatedFromServer) localUpdatedRef.current = updatedFromServer;
        setHasRemoteUpdate(false);
        ignoreRemoteUntilRef.current = Date.now() + 12_000;
    }, []);

    // ===== Collabs UI =====
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [collabUsers, setCollabUsers] = useState<{ id: string; name?: string; email?: string }[]>([]);
    const [userQuery, setUserQuery] = useState("");
    const [userSuggestions, setUserSuggestions] = useState<{ id: string; name?: string; email?: string }[]>([]);
    const [suggestLoading, setSuggestLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState<{ id: string; label: string } | null>(null);

    // ✅ Si le parent met à jour plan.siteImage (URL) après save/reload, on resync le draft
    useEffect(() => {
        if (normalizedPlan.siteImage) {
            setSiteImageDraft(normalizedPlan.siteImage);
        }
    }, [normalizedPlan.siteImage]);

    // ===== Reset editor when plan changes =====
    useEffect(() => {
        history.clear(normalizedPlan);
        setSelectedElement(null);
        setSelectedPath(null);
        setDrawingPath(null);
        setActiveElementType(null);
        setActivePathType(null);

        setIsPublic(!!normalizedPlan.isPublic);
        setIsReadOnlyMode(!!normalizedPlan.isReadOnly);

        // image :
        // - si URL/data présente dans le plan -> on l'affiche
        // - sinon si local-only -> on tente de recharger depuis indexedDB
        setSiteImageDraft(normalizedPlan.siteImage ?? null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [normalizedPlan.id]);

    useEffect(() => {
        let alive = true;

        const run = async () => {
            if (isCloud) return; // cloud : pas d’indexedDB
            if (normalizedPlan.siteImage) return; // déjà une image
            const key = `plan:${normalizedPlan.id}:siteImage`;

            try {
                const img = await LocalImageStore.get(key);
                if (!alive) return;
                if (img) setSiteImageDraft(img);
            } catch (e) {
                console.warn("IndexedDB load image failed:", e);
            }
        };

        run();
        return () => { alive = false; };
    }, [isCloud, normalizedPlan.id, normalizedPlan.siteImage]);

    useEffect(() => {
        if (isCloud) return;
        // si on n’est plus cloud => on purge tout ce qui est collab/presence
        setActiveUsers([]);
        setHasRemoteUpdate(false);
        setRemoteMeta(null);
        sessionIdRef.current = null;
    }, [isCloud]);

    useEffect(() => {
        if (!isOwner) return;

        const q = userQuery.trim();
        if (!q) {
            setUserSuggestions([]);
            return;
        }

        const t = setTimeout(async () => {
            setSuggestLoading(true);
            const res = await pocketbaseService.searchUsers(q);
            setSuggestLoading(false);
            setUserSuggestions(res.data ?? []);
        }, 300);

        return () => clearTimeout(t);
    }, [userQuery, isOwner]);

    useEffect(() => {
        if (!isOwner) return;

        const ids = formData.collaborators ?? [];
        pocketbaseService.getUsersByIds(ids).then((res) => {
            if (res.data) setCollabUsers(res.data);
        });
    }, [isOwner, formData.collaborators]);

    // ===== Presence polling =====
    useEffect(() => {
        if (!collabEnabled) return;     // ✅ privé / RO => pas de session
        if (!isLoggedIn) return;        // ✅ pas loggué => pas de session
        if (!cloudId) return;

        const plan = cloudId;
        const clientId = clientIdRef.current;
        const mode: "view" | "edit" = isReadOnly ? "view" : "edit";

        let alive = true;

        const refresh = async () => {
            const { data } = await pocketbaseService.listActiveSessions(plan, 40);
            if (!alive || !data) return;

            // dédoublonner par userId
            const map = new Map<string, any>();
            for (const s of data) {
                const uid = typeof s.userId === "string" ? s.userId : String(s.userId);
                const prev = map.get(uid);

                if (!prev) map.set(uid, s);
                else {
                    const prevTs = Date.parse(prev.lastSeen || prev.updated || "");
                    const curTs = Date.parse(s.lastSeen || s.updated || "");
                    if (curTs >= prevTs) map.set(uid, s);
                }
            }
            setActiveUsers(Array.from(map.values()));
        };

        const ping = async () => {
            const res = await pocketbaseService.upsertPlanSession(plan, mode, clientId);
            if (res?.error) {
                console.error("PING SESSION ERROR:", res.error);
                return;
            }
            if (res?.data?.id) sessionIdRef.current = res.data.id;
        };

        ping().then(refresh);

        const pingInterval = setInterval(ping, 20000);
        const refreshInterval = setInterval(refresh, 5000);

        const burstStart = Date.now();
        const burstInterval = setInterval(() => {
            if (Date.now() - burstStart > 10000) return;
            refresh();
        }, 1500);

        const onFocus = () => refresh();
        const onVis = () => {
            if (!document.hidden) refresh();
        };

        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVis);

        return () => {
            alive = false;
            clearInterval(pingInterval);
            clearInterval(refreshInterval);
            clearInterval(burstInterval);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [collabEnabled, cloudId, isReadOnly, isLoggedIn]);

    // ===== Remote update polling =====
    useEffect(() => {
        if (!isCloud || !cloudId) return;

        let alive = true;

        const check = async () => {
            if (Date.now() < ignoreRemoteUntilRef.current) return;

            const res = await pocketbaseService.getPlanMeta(cloudId);
            if (!alive) return;

            // ✅ plan supprimé => on coupe cloud localement
            if (res.error === "__DELETED__") {
                setIsPublic(false);
                setIsReadOnlyMode(false);

                history.set({
                    ...history.state,
                    cloudId: undefined,
                    isPublic: false,
                    isReadOnly: false,
                    collaborators: [],
                } as any);

                setHasRemoteUpdate(false);
                setRemoteMeta(null);
                localUpdatedRef.current = null;
                sessionIdRef.current = null;
                return;
            }

            if (!res.data) return;

            const { updated, lastEditedByName, lastEditedAt } = res.data;

            if (!localUpdatedRef.current) {
                localUpdatedRef.current = updated;
                setRemoteMeta({ lastEditedByName, lastEditedAt });
                return;
            }

            const justSavedRecently = Date.now() - lastLocalSaveAtRef.current < 2000;

            if (updated && updated !== localUpdatedRef.current) {
                if (justSavedRecently) {
                    localUpdatedRef.current = updated;
                    setRemoteMeta({ lastEditedByName, lastEditedAt });
                    setHasRemoteUpdate(false);
                    return;
                }

                setHasRemoteUpdate(true);
                setRemoteMeta({ lastEditedByName, lastEditedAt });
            }
        };

        let t: number | null = null;
        const schedule = () => {
            const interval = document.visibilityState === "visible" ? 5000 : 20000;
            t = window.setTimeout(async () => {
                await check();
                schedule();
            }, interval);
        };

        const onFocus = () => check();
        const onVis = () => {
            if (!document.hidden) check();
        };

        check();
        schedule();
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVis);

        return () => {
            alive = false;
            if (t) window.clearTimeout(t);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [isCloud, cloudId, history]);

    // ===== Mutations =====
    const updateFormData = useCallback(
        (updates: Partial<SkiPlan>) => {
            const next = { ...formData, ...updates };

            // ⚠️ on ne veut PAS de base64 dans l'historique (undo/redo) ni dans localStorage
            if (typeof next.siteImage === "string" && next.siteImage.startsWith("data:")) {
                next.siteImage = null as any;
            }
            if (typeof next.thumbnail === "string" && next.thumbnail.startsWith("data:")) {
                // thumbnail OK à garder dans le plan; mais on évite de la dupliquer dans history en boucle
                // -> on la garde quand même, elle est petite, mais tu peux décider de la nuller ici si besoin.
                // next.thumbnail = next.thumbnail;
            }

            history.set(next);
        },
        [history, formData]
    );

    const handleImageUpload = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (event) => {
                const originalImage = event.target?.result as string;

                compressImage(originalImage, 3840, 2160, 0.92)
                    .then(async (compressedImage) => {
                        setSiteImageDraft(compressedImage); // ✅ base64 en RAM
                        updateFormData({ siteImage: null }); // ✅ plan "light"
                        // ✅ on stocke direct en IndexedDB (plan local)
                        if (!formData.cloudId) {
                            const key = `plan:${formData.id}:siteImage`;
                            try {
                                await LocalImageStore.set(key, compressedImage);
                            } catch (err) {
                                console.warn("IndexedDB save image failed:", err);
                            }
                        }
                    })
                    .catch(async () => {
                        setSiteImageDraft(originalImage);
                        updateFormData({ siteImage: null });
                        if (!formData.cloudId) {
                            const key = `plan:${formData.id}:siteImage`;
                            try {
                                await LocalImageStore.set(key, originalImage);
                            } catch (err) {
                                console.warn("IndexedDB save image failed:", err);
                            }
                        }
                    });
            };
            reader.readAsDataURL(file);
        },
        [updateFormData, formData.cloudId, formData.id]
    );

    const reloadFromCloud = useCallback(async () => {
        if (!isCloud || !cloudId) return;

        const res = await pocketbaseService.getPlanWithImage(cloudId);
        if (!res.data?.plan) return;

        history.set(res.data.plan);
        setSiteImageDraft(res.data.plan.siteImage ?? null);

        setSelectedElement(null);
        setSelectedPath(null);
        setDrawingPath(null);

        setIsPublic(!!res.data.plan.isPublic);
        setIsReadOnlyMode(!!res.data.plan.isReadOnly);

        if (res.data.record?.updated) localUpdatedRef.current = res.data.record.updated;
        setHasRemoteUpdate(false);
    }, [isCloud, cloudId, history]);

    const handleAddCollaborator = useCallback(
        async (userId: string) => {
            if (!isCloud || !cloudId) return;

            setInviteError(null);

            const add = await pocketbaseService.addCollaborator(cloudId, userId);
            if (!add.data) {
                setInviteError(add.error || "Impossible d'ajouter");
                return;
            }

            updateFormData({ collaborators: add.data.collaborators } as any);

            const users = await pocketbaseService.getUsersByIds(add.data.collaborators ?? []);
            if (users.data) setCollabUsers(users.data);

            ackLocalSave(add.data.updated);

            setUserQuery("");
            setUserSuggestions([]);
            setSelectedUser(null);
        },
        [isCloud, cloudId, updateFormData, ackLocalSave]
    );

    const handleRemoveCollaborator = useCallback(
        async (userId: string) => {
            if (!isCloud || !cloudId) return;
            setInviteError(null);

            const res = await pocketbaseService.removeCollaborator(cloudId, userId);
            if (!res.data) {
                setInviteError(res.error || "Impossible de retirer");
                return;
            }

            const ids = res.data.collaborators ?? [];
            updateFormData({ collaborators: ids } as any);

            const users = await pocketbaseService.getUsersByIds(ids);
            if (users.data) setCollabUsers(users.data);

            ackLocalSave(res.data.updated);
        },
        [isCloud, cloudId, updateFormData, ackLocalSave]
    );

    const getDefaultLabel = useCallback((type: SiteElement["type"]): string => {
        const labels: Record<SiteElement["type"], string> = {
            start: "Départ",
            finish: "Arrivée",
            "feed-zone": "Ravitaillement",
            "wax-zone": "Zone fartage",
            timing: "Chronométrage",
            exchange: "Zone échange",
            medical: "Poste médical",
            spectator: "Zone spectateurs",
            "loop-marker": "Marqueur boucle",
            camera: "Caméra",
            "weigh-zone": "Zone de pesée",
            parking: "Parking",
            "locker-room": "Vestiaires",
            "warmup-zone": "Zone d'échauffement",
            tribune: "Tribune",
            wc: "WC",
            "ski-drop": "Dépose de ski",
            "danger-zone": "Zone de danger",
            custom: "Personnalisé",
            text: "Texte",
        };
        return labels[type] || "Inconnu";
    }, []);

    const handleCanvasClick = useCallback(
        (x: number, y: number) => {
            if (isReadOnly) return;

            if (toolMode === "element" && activeElementType) {
                if (activeElementType === "custom") {
                    setShowCustomDialog(true);
                    return;
                }

                const newElement: SiteElement = {
                    id: Date.now().toString(),
                    type: activeElementType,
                    position: { x, y },
                    label: getDefaultLabel(activeElementType),
                    notes: "",
                };

                updateFormData({ elements: [...formData.elements, newElement] });
                setActiveElementType(null);
                return;
            }

            if (toolMode === "draw" && activePathType) {
                if (!drawingPath) {
                    const defaults: Record<Path["type"], any> = {
                        course: { smooth: true, dashArray: undefined, strokeWidth: 1.5, opacity: 0.5 },
                        net: { smooth: true, dashArray: undefined, strokeWidth: 0.4, opacity: 1 },
                        barrier: { smooth: false, dashArray: undefined, strokeWidth: 0.4, opacity: 1 },
                        "v-board": { smooth: true, dashArray: "0.5,1", strokeWidth: 0.4, opacity: 1 },
                        sapinettes: { smooth: false, dashArray: "0.05,1", strokeWidth: 0.2, opacity: 1 },
                        custom: { smooth: false, dashArray: undefined, strokeWidth: 0.5, opacity: 1 },
                    };

                    const d = defaults[activePathType];
                    setDrawingPath({
                        id: Date.now().toString(),
                        type: activePathType,
                        points: [{ x, y }],
                        smooth: d.smooth,
                        dashArray: d.dashArray,
                        strokeWidth: d.strokeWidth,
                        opacity: d.opacity,
                    });
                } else {
                    setDrawingPath({ ...drawingPath, points: [...drawingPath.points, { x, y }] });
                }
                return;
            }

            setSelectedElement(null);
            setSelectedPath(null);
        },
        [
            isReadOnly,
            toolMode,
            activeElementType,
            activePathType,
            drawingPath,
            formData.elements,
            updateFormData,
            getDefaultLabel,
        ]
    );

    const handleFinishDrawing = useCallback(() => {
        if (drawingPath && drawingPath.points.length > 1) {
            updateFormData({ paths: [...formData.paths, drawingPath] });
            setDrawingPath(null);
            setActivePathType(null);
            setSelectedPath(null);
        }
    }, [drawingPath, formData.paths, updateFormData]);

    const handleCancelDrawing = useCallback(() => {
        setDrawingPath(null);
        setActivePathType(null);
    }, []);

    const handleElementClick = useCallback((element: SiteElement) => {
        setSelectedElement(element);
        setSelectedPath(null);
        setActiveElementType(null);
    }, []);

    const handlePathClick = useCallback((path: Path) => {
        setSelectedPath(path);
        setSelectedElement(null);
    }, []);

    const handleUpdateElement = useCallback(
        (updates: Partial<SiteElement>) => {
            if (!selectedElement) return;

            updateFormData({
                elements: formData.elements.map((e) => (e.id === selectedElement.id ? { ...e, ...updates } : e)),
            });
            setSelectedElement({ ...selectedElement, ...updates });
        },
        [selectedElement, formData.elements, updateFormData]
    );

    const handleUpdatePath = useCallback(
        (updates: Partial<Path>) => {
            if (!selectedPath) return;

            updateFormData({
                paths: formData.paths.map((p) => (p.id === selectedPath.id ? { ...p, ...updates } : p)),
            });
            setSelectedPath({ ...selectedPath, ...updates });
        },
        [selectedPath, formData.paths, updateFormData]
    );

    const handleDeleteElement = useCallback(
        (id: string) => {
            updateFormData({ elements: formData.elements.filter((e) => e.id !== id) });
            if (selectedElement?.id === id) setSelectedElement(null);
        },
        [formData.elements, selectedElement, updateFormData]
    );

    const handleDeletePath = useCallback(
        (id: string) => {
            updateFormData({ paths: formData.paths.filter((p) => p.id !== id) });
            if (selectedPath?.id === id) setSelectedPath(null);
        },
        [formData.paths, selectedPath, updateFormData]
    );

    const handleCreateCustomElement = useCallback(
        (label: string, color: string) => {
            const newElement: SiteElement = {
                id: Date.now().toString(),
                type: "custom",
                position: { x: 50, y: 50 },
                label,
                color,
                notes: "",
            } as any;

            updateFormData({ elements: [...formData.elements, newElement] });
            setShowCustomDialog(false);
            setActiveElementType(null);
        },
        [formData.elements, updateFormData]
    );

    const handleElementMove = useCallback(
        (id: string, x: number, y: number) => {
            updateFormData({
                elements: formData.elements.map((e) => (e.id === id ? { ...e, position: { x, y } } : e)),
            });
            if (selectedElement?.id === id) setSelectedElement({ ...selectedElement, position: { x, y } });
        },
        [formData.elements, selectedElement, updateFormData]
    );

    const handlePathPointMove = useCallback(
        (pathId: string, pointIndex: number, x: number, y: number) => {
            updateFormData({
                paths: formData.paths.map((p) =>
                    p.id === pathId ? { ...p, points: p.points.map((pt, i) => (i === pointIndex ? { x, y } : pt)) } : p
                ),
            });

            if (selectedPath?.id === pathId) {
                const updatedPoints = selectedPath.points.map((pt, i) => (i === pointIndex ? { x, y } : pt));
                setSelectedPath({ ...selectedPath, points: updatedPoints });
            }
        },
        [formData.paths, selectedPath, updateFormData]
    );

    const handleExportPDF = useCallback(async () => {
        try {
            document.body.setAttribute("data-exporting", "true");

            await siteCanvasRef.current?.resetView();
            await waitUiUpdate();
            await waitUiUpdate();
            await waitNextPaint();
            await waitNextPaint();

            const planForExport = { ...formData, siteImage: effectiveImage };
            const preview = await generatePreview(planForExport);

            document.body.removeAttribute("data-exporting");
            setPreviewImage(preview);
            setShowPDFPreview(true);
        } catch (error) {
            document.body.removeAttribute("data-exporting");
            console.error("Erreur lors de la génération de l'aperçu:", error);
            alert("Erreur lors de la génération de l'aperçu du PDF");
        }
    }, [formData, effectiveImage]);

    const handleConfirmExportPDF = useCallback(
        async (includeMetadata: boolean, includeLegend: boolean) => {
            setShowPDFPreview(false);

            await siteCanvasRef.current?.resetView();
            await waitUiUpdate();
            await waitUiUpdate();
            await waitNextPaint();
            await waitNextPaint();

            const planForExport = { ...formData, siteImage: effectiveImage };
            await exportToPDF(planForExport, includeMetadata, includeLegend);
        },
        [formData, effectiveImage]
    );

    const handleExportJSON = useCallback(() => {
        const dataStr = JSON.stringify(formData, null, 2);
        const dataBlob = new Blob([dataStr], { type: "application/json" });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${formData.name || "plan-ski"}-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [formData]);

    const handleImportJSON = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const jsonData = JSON.parse(event.target?.result as string);
                    updateFormData(jsonData);
                } catch (error) {
                    console.error("Erreur lors de l'import JSON:", error);
                    alert("Erreur: Le fichier JSON est invalide");
                }
            };
            reader.readAsText(file);
        },
        [updateFormData]
    );

    // ===== Save (local + cloud) =====
    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            if (isReadOnly) return;

            // Interdire publication si pas loggué
            if ((isPublic || isReadOnlyMode) && !currentUserId) {
                alert("Connectez-vous pour publier un plan.");
                return;
            }

            // 1) thumbnail best effort
            let thumbnailDataUrl: string | null = null;

            if (!!effectiveImage && typeof toPng === "function") {
                try {
                    const canvasContainer = document.querySelector("[data-ski-canvas]");
                    if (canvasContainer) {
                        thumbnailDataUrl = await toPng(canvasContainer as HTMLElement, {
                            quality: 0.25,
                            pixelRatio: 0.2,
                            cacheBust: true,
                            skipFonts: true,
                            filter: (node) => {
                                if (node instanceof HTMLElement) {
                                    if (node.hasAttribute("data-exclude-from-thumbnail")) return false;
                                    if (node.classList?.contains("toolbar") || node.classList?.contains("properties-panel")) return false;
                                }
                                return true;
                            },
                        });
                        if (thumbnailDataUrl && thumbnailDataUrl.length > 250_000) thumbnailDataUrl = null;
                    }
                } catch (error) {
                    console.error("Erreur vignette:", error);
                    thumbnailDataUrl = null;
                }
            }

            // 2) base commun (sans image)
            const base: SkiPlan = {
                ...formData,
                thumbnail: thumbnailDataUrl ?? formData.thumbnail ?? null,
                userId: formData.userId ?? currentUserId,
                isPublic,
                isReadOnly: isPublic ? isReadOnlyMode : false,
            };

            // 3) plan local (jamais base64 dans localStorage)
            let planForLocal: SkiPlan = { ...base, siteImage: null };

            // 4) si base64 => IndexedDB (toujours) + pas dans localStorage
            const hasBase64 = typeof effectiveImage === "string" && effectiveImage.startsWith("data:");
            if (hasBase64) {
                const key = `plan:${base.id}:siteImage`;
                try {
                    await LocalImageStore.set(key, effectiveImage as string);
                } catch (err) {
                    console.warn("IndexedDB save image failed:", err);
                }
            } else {
                // si c'est déjà une URL, on peut la garder en local
                planForLocal.siteImage = (typeof effectiveImage === "string" ? effectiveImage : null) as any;
            }

            // 5) Cloud sync ?
            // Règle simple : si loggué ET (on a déjà un cloudId OU on a une image à pousser OU on publie)
            const shouldSyncCloud =
                !!currentUserId && (
                    !!base.cloudId ||                 // déjà dans PB
                    !!effectiveImage ||               // on a une image
                    !!isPublic || !!isReadOnlyMode    // on veut publier
                );

            if (shouldSyncCloud) {
                // 1) save plan light
                const lightForCloud: SkiPlan = { ...base, siteImage: null };

                const res = await pocketbaseService.savePlanToCloud({
                    cloudId: base.cloudId,
                    plan: lightForCloud,
                    isPublic: !!isPublic,
                    isReadOnly: !!(isPublic ? isReadOnlyMode : false),
                });

                if (res.error || !res.data) {
                    alert(res.error || "Erreur sauvegarde cloud");
                    return;
                }

                const cloudId = res.data.cloudId;
                ackLocalSave(res.data.updated);

                // 2) upload image si base64 en RAM
                if (typeof effectiveImage === "string" && effectiveImage.startsWith("data:")) {
                    const up = await pocketbaseService.uploadImage(effectiveImage, cloudId);
                    if (up.error) {
                        alert(up.error || "Erreur upload image");
                        return;
                    }
                }

                // 3) reload image URL (token si privé)
                const opened = await pocketbaseService.getPlanWithImage(cloudId);
                const latestUrl = opened.data?.plan?.siteImage ?? null;

                if (latestUrl) {
                    setSiteImageDraft(latestUrl);
                    updateFormData({ siteImage: latestUrl });
                    planForLocal = { ...planForLocal, siteImage: latestUrl };
                }

                // 4) cloudId stable côté local
                planForLocal = { ...planForLocal, cloudId };
            }

            // 6) save local (safe)
            await onSave(planForLocal);
        },
        [
            formData,
            effectiveImage,
            onSave,
            isReadOnly,
            isPublic,
            isReadOnlyMode,
            currentUserId,
            ackLocalSave,
            updateFormData,
        ]
    );

    const handleDuplicateElement = useCallback(() => {
        if (!selectedElement) return;

        const newElement: SiteElement = {
            ...selectedElement,
            id: Date.now().toString(),
            position: { x: selectedElement.position.x + 5, y: selectedElement.position.y + 5 },
        };

        updateFormData({ elements: [...formData.elements, newElement] });
        setSelectedElement(newElement);
    }, [selectedElement, formData.elements, updateFormData]);

    const handleDuplicatePlan = useCallback(() => {
        const duplicatedPlan: SkiPlan = {
            ...formData,
            id: Date.now().toString(),
            name: `${formData.name} (copie)`,
            isPublic: false,
            isReadOnly: false,
            cloudId: undefined,
            userId: currentUserId,
            userName: undefined,
            collaborators: [],
        };

        onSave(duplicatedPlan);
    }, [formData, onSave, currentUserId]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (
                (e.target as HTMLElement).tagName === "INPUT" ||
                (e.target as HTMLElement).tagName === "TEXTAREA" ||
                (e.target as HTMLElement).tagName === "SELECT"
            ) {
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
                e.preventDefault();
                history.undo();
            } else if (
                ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "z") ||
                ((e.ctrlKey || e.metaKey) && e.key === "y")
            ) {
                e.preventDefault();
                history.redo();
            } else if ((e.ctrlKey || e.metaKey) && e.key === "d" && selectedElement) {
                e.preventDefault();
                handleDuplicateElement();
            } else if ((e.key === "Delete" || e.key === "Backspace") && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                if (selectedElement) handleDeleteElement(selectedElement.id);
                else if (selectedPath) handleDeletePath(selectedPath.id);
            } else if (e.key === "Escape") {
                setSelectedElement(null);
                setSelectedPath(null);
                setActiveElementType(null);
                setActivePathType(null);
                handleCancelDrawing();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [history, selectedElement, selectedPath, handleDuplicateElement, handleCancelDrawing, handleDeleteElement, handleDeletePath]);

    // ===== RENDER =====
    return (
        <div className={isFullscreen ? "fixed inset-0 z-50 bg-white flex flex-col" : "min-h-screen bg-gray-50"}>
            {/* Header */}
            <header className={isFullscreen ? "hidden" : "bg-white shadow-sm border-b border-gray-200"}>
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <h1 className="text-gray-900">Éditeur de Plan de Compétition</h1>

                        <div className="flex items-center gap-3">
                            {collabEnabled && (
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border bg-gray-50 border-gray-200 text-gray-700">
                                    <User className="w-4 h-4" />
                                    {activeUsers.length === 0 ? "Seul sur le plan" : `${activeUsers.length} actif${activeUsers.length > 1 ? "s" : ""}`}
                                </span>
                            )}

                            {!isPlanPublic ? (
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border bg-gray-50 border-gray-200 text-gray-700">
                                    <Eye className="w-4 h-4" />
                                    Privé
                                </span>
                            ) : isPlanReadOnlyMode ? (
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border bg-amber-50 border-amber-200 text-amber-800">
                                    <Lock className="w-4 h-4" />
                                    Public — lecture seule
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border bg-blue-50 border-blue-200 text-blue-800">
                                    <Globe className="w-4 h-4" />
                                    Public — collaboratif
                                </span>
                            )}
                        </div>
                    </div>

                    {isReadOnly && (
                        <div className="mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 px-4 py-2 rounded-lg">
                            <Eye className="w-5 h-5 text-amber-600" />
                            <p className="text-sm text-amber-900">
                                <span className="font-semibold">Mode lecture seule</span> — Plan créé par un autre utilisateur
                            </p>
                            {isLoggedIn && (
                                <button
                                    type="button"
                                    onClick={handleDuplicatePlan}
                                    className="ml-auto px-3 py-1.5 rounded-lg border border-amber-300 bg-white hover:bg-amber-100 transition-colors text-sm"
                                >
                                    Dupliquer
                                </button>
                            )}
                        </div>
                    )}

                    {/* Collaborateurs (owner only) */}
                    {isOwner && collabEnabled && (
                        <div className="mt-3 bg-gray-50 border border-gray-200 px-4 py-3 rounded-lg">
                            <div className="grid grid-cols-[360px_1fr] gap-6 items-start">
                                <div className="w-[420px]">
                                    <div className="text-sm font-medium text-gray-700 mb-1">Inviter un utilisateur</div>

                                    <div className="flex items-center gap-2">
                                        <div className="relative w-full">
                                            <input
                                                value={userQuery}
                                                onChange={(e) => {
                                                    setUserQuery(e.target.value);
                                                    setSelectedUser(null);
                                                }}
                                                placeholder="Rechercher un utilisateur…"
                                                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-full"
                                            />

                                            {(() => {
                                                const existingIds = new Set(formData.collaborators ?? []);
                                                const suggestions = (userSuggestions ?? []).filter(
                                                    (u) => u.id !== currentUserId && !existingIds.has(u.id)
                                                );

                                                const showDropdown = (suggestLoading || suggestions.length > 0) && userQuery.trim().length > 0;
                                                if (!showDropdown) return null;

                                                return (
                                                    <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-56 overflow-auto">
                                                        {suggestLoading && <div className="px-3 py-2 text-sm text-gray-500">Recherche…</div>}

                                                        {!suggestLoading && suggestions.length === 0 && (
                                                            <div className="px-3 py-2 text-sm text-gray-500">Aucun résultat</div>
                                                        )}

                                                        {!suggestLoading &&
                                                            suggestions.map((u) => (
                                                                <button
                                                                    key={u.id}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const label = u.name ?? "Utilisateur";
                                                                        setSelectedUser({ id: u.id, label });
                                                                        setUserQuery(label);
                                                                        setUserSuggestions([]);
                                                                    }}
                                                                    className="w-full text-left px-3 py-2 hover:bg-gray-50"
                                                                    title="Cliquer pour sélectionner"
                                                                >
                                                                    <span className="text-sm text-gray-800">{u.name ?? u.email}</span>
                                                                </button>
                                                            ))}
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        <button
                                            type="button"
                                            disabled={!selectedUser}
                                            onClick={() => selectedUser && handleAddCollaborator(selectedUser.id)}
                                            className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
                                        >
                                            Ajouter
                                        </button>
                                    </div>

                                    {inviteError && <div className="mt-2 text-sm text-red-600">{inviteError}</div>}
                                </div>

                                <div className="justify-self-end text-right">
                                    <div className="text-sm font-medium text-gray-700 mb-1">Utilisateurs autorisés</div>

                                    {collabUsers.length === 0 ? (
                                        <div className="text-sm text-gray-500">Aucun collaborateur pour l’instant.</div>
                                    ) : (
                                        <div className="flex flex-wrap gap-2 justify-end">
                                            {collabUsers.map((u) => (
                                                <button
                                                    key={u.id}
                                                    type="button"
                                                    onClick={() => handleRemoveCollaborator(u.id)}
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border bg-white hover:bg-red-50 hover:border-red-200 text-sm"
                                                    title="Cliquer pour retirer"
                                                >
                                                    <span className="text-gray-800">{u.name ?? u.email ?? "Utilisateur"}</span>
                                                    <span className="text-gray-400">✕</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            <form onSubmit={handleSubmit} className={isFullscreen ? "flex-1 flex flex-col overflow-hidden h-full" : ""}>
                {/* Caractéristiques de la course */}
                {!isFullscreen && (
                    <div className="border-b border-gray-200 bg-gray-50 px-6 py-3 flex-shrink-0">
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Nom de la course</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => updateFormData({ name: e.target.value })}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Course"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Lieu</label>
                                <input
                                    type="text"
                                    value={formData.location}
                                    onChange={(e) => updateFormData({ location: e.target.value })}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Bessans"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Date</label>
                                <input
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => updateFormData({ date: e.target.value })}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Type</label>
                                <select
                                    value={formData.raceType}
                                    onChange={(e) => updateFormData({ raceType: e.target.value })}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">Sélectionner...</option>
                                    <option value="Sprint">Sprint</option>
                                    <option value="Distance">Distance</option>
                                    <option value="Relais">Relais</option>
                                    <option value="Poursuite">Poursuite</option>
                                    <option value="Mass Start">Mass Start</option>
                                    <option value="Skiathlon">Skiathlon</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Technique</label>
                                <select
                                    value={formData.technique || ""}
                                    onChange={(e) => updateFormData({ technique: e.target.value as any })}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">Sélectionner...</option>
                                    <option value="classique">Classique</option>
                                    <option value="skating">Skating</option>
                                    <option value="libre">Libre</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Distance (m)</label>
                                <input
                                    type="number"
                                    value={formData.totalDistance || ""}
                                    onChange={(e) => updateFormData({ totalDistance: parseInt(e.target.value) || undefined })}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="15000"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Tours</label>
                                <input
                                    type="number"
                                    value={formData.numLaps || ""}
                                    onChange={(e) => updateFormData({ numLaps: parseInt(e.target.value) || undefined })}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="3"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Zone principale */}
                <div className={isFullscreen ? "flex-1 flex flex-col overflow-hidden" : "p-6 space-y-4"}>
                    {!effectiveImage && (
                        <div className="flex items-center justify-center">
                            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                            >
                                <Upload className="w-5 h-5" />
                                Charger une image du site
                            </button>
                        </div>
                    )}

                    {effectiveImage && !isFullscreen && !isReadOnly && (
                        <div className="space-y-4">
                            <div className="flex gap-2 bg-gray-100 p-1 rounded-lg w-fit mx-auto">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setToolMode("element");
                                        setActivePathType(null);
                                        handleCancelDrawing();
                                    }}
                                    className={`px-6 py-2 rounded-lg transition-colors ${toolMode === "element" ? "bg-white shadow-sm" : "hover:bg-gray-200"
                                        }`}
                                >
                                    Éléments
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setToolMode("draw");
                                        setActiveElementType(null);
                                    }}
                                    className={`px-6 py-2 rounded-lg transition-colors ${toolMode === "draw" ? "bg-white shadow-sm" : "hover:bg-gray-200"
                                        }`}
                                >
                                    Dessiner
                                </button>
                            </div>

                            <div>
                                {toolMode === "element" ? (
                                    <ElementToolbar
                                        activeType={activeElementType}
                                        onSelectType={setActiveElementType}
                                        customIcons={(formData as any).customIcons}
                                        onCustomIconChange={(type, iconName) => {
                                            updateFormData({
                                                customIcons: {
                                                    ...(formData as any).customIcons,
                                                    [type]: iconName,
                                                } as any,
                                            } as any);
                                        }}
                                    />
                                ) : (
                                    <DrawingToolbar
                                        activeType={activePathType}
                                        onSelectType={setActivePathType}
                                        isDrawing={!!drawingPath}
                                        onFinish={handleFinishDrawing}
                                        onCancel={handleCancelDrawing}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    <div className={isFullscreen ? "flex-1 relative" : "relative"}>
                        <SiteCanvas
                            ref={siteCanvasRef}
                            image={effectiveImage}
                            elements={formData.elements}
                            paths={formData.paths}
                            drawingPath={drawingPath}
                            selectedElement={selectedElement}
                            selectedPath={selectedPath}
                            activeElementType={activeElementType}
                            activePathType={activePathType}
                            onCanvasClick={handleCanvasClick}
                            onElementClick={handleElementClick}
                            onPathClick={handlePathClick}
                            onElementMove={handleElementMove}
                            onPathPointMove={handlePathPointMove}
                            isFullscreen={isFullscreen}
                            toolMode={toolMode}
                            onToolModeChange={setToolMode}
                            onActiveElementTypeChange={setActiveElementType}
                            onActivePathTypeChange={setActivePathType}
                            onFinishDrawing={handleFinishDrawing}
                            onCancelDrawing={handleCancelDrawing}
                            customIcons={(formData as any).customIcons}
                            isReadOnly={isReadOnly}
                        />

                        {(selectedElement || selectedPath) && !!effectiveImage && (
                            <div
                                className={`absolute transition-all duration-300 bg-white border-2 border-blue-500 rounded-lg shadow-2xl z-20 ${isPropertiesPanelMinimized
                                        ? "bottom-4 right-4 w-auto"
                                        : `bottom-4 left-4 right-4 ${isFullscreen ? "max-h-[25vh]" : "max-h-[35vh] max-w-6xl mx-auto"} overflow-y-auto animate-slideUp`
                                    }`}
                            >
                                {isPropertiesPanelMinimized ? (
                                    <button
                                        type="button"
                                        onClick={() => setIsPropertiesPanelMinimized(false)}
                                        className="p-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
                                        title="Afficher les propriétés"
                                    >
                                        <Info className="w-4 h-4 text-blue-600" />
                                        <span className="text-sm text-gray-700">
                                            {selectedElement ? "Propriétés élément" : "Propriétés tracé"}
                                        </span>
                                    </button>
                                ) : (
                                    <div className="p-3">
                                        {selectedElement && (
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="text-sm font-semibold text-gray-900">Propriétés de l&apos;élément</h3>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => setIsPropertiesPanelMinimized(true)}
                                                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                            title="Minimiser"
                                                        >
                                                            <Minimize className="w-4 h-4 text-gray-600" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setSelectedElement(null)}
                                                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                            title="Fermer (Échap)"
                                                        >
                                                            <X className="w-4 h-4 text-gray-600" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <ElementProperties
                                                    element={selectedElement}
                                                    onUpdate={handleUpdateElement}
                                                    onDelete={() => handleDeleteElement(selectedElement.id)}
                                                />
                                            </div>
                                        )}

                                        {selectedPath && (
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="text-sm font-semibold text-gray-900">Propriétés du tracé</h3>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => setIsPropertiesPanelMinimized(true)}
                                                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                            title="Minimiser"
                                                        >
                                                            <Minimize className="w-4 h-4 text-gray-600" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setSelectedPath(null)}
                                                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                            title="Fermer (Échap)"
                                                        >
                                                            <X className="w-4 h-4 text-gray-600" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <PathProperties path={selectedPath} onUpdate={handleUpdatePath} onDelete={() => handleDeletePath(selectedPath.id)} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {effectiveImage && !isFullscreen && (
                        <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                            <p className="text-sm text-gray-600">
                                <span className="font-semibold">{formData.elements.length}</span> élément(s) •{" "}
                                <span className="font-semibold">{formData.paths.length}</span> tracé(s)
                            </p>
                            <div className="flex items-center gap-2">
                                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <Upload className="w-4 h-4" />
                                    Changer l&apos;image
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Banner "nouvelle version" */}
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                    {hasRemoteUpdate && (
                        <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 px-4 py-3 rounded-lg">
                            <div className="text-sm text-blue-900">
                                <span className="font-semibold">Nouvelle version disponible</span>
                                {remoteMeta?.lastEditedByName && (
                                    <span className="ml-2 opacity-80">
                                        (par {remoteMeta.lastEditedByName}
                                        {remoteMeta.lastEditedAt ? ` · ${new Date(remoteMeta.lastEditedAt).toLocaleString()}` : ""})
                                    </span>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={reloadFromCloud}
                                className="px-3 py-1.5 rounded-lg border border-blue-300 bg-white hover:bg-blue-100 transition-colors text-sm"
                            >
                                Recharger
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer actions */}
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-between gap-3">
                    {/* GAUCHE */}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={handleExportPDF}
                            disabled={!effectiveImage}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Download className="w-4 h-4" />
                            PDF <span className="hidden md:inline">Exporter PDF</span>
                        </button>

                        {!isReadOnly && (
                            <>
                                <button
                                    type="button"
                                    onClick={handleExportJSON}
                                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <FileJson className="w-4 h-4" />
                                    <span className="hidden lg:inline">Exporter JSON</span>
                                </button>

                                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                                    <Upload className="w-4 h-4" />
                                    <span className="hidden lg:inline">Importer JSON</span>
                                    <input type="file" accept=".json" onChange={handleImportJSON} className="hidden" />
                                </label>
                            </>
                        )}
                    </div>

                    {/* CENTRE */}
                    <div className="flex items-center justify-between">
                        {!isReadOnly && (
                            <>
                                <button
                                    type="button"
                                    onClick={history.undo}
                                    disabled={!history.canUndo}
                                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Annuler (Ctrl+Z)"
                                >
                                    <Undo2 className="w-4 h-4" />
                                </button>

                                <button
                                    type="button"
                                    onClick={history.redo}
                                    disabled={!history.canRedo}
                                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Refaire (Ctrl+Y)"
                                >
                                    <Redo2 className="w-4 h-4" />
                                </button>

                                {selectedElement && (
                                    <button
                                        type="button"
                                        onClick={handleDuplicateElement}
                                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                                        title="Dupliquer (Ctrl+D)"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </button>
                                )}
                            </>
                        )}

                        {!isFullscreen ? (
                            <button
                                type="button"
                                onClick={() => setIsFullscreen(true)}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                                title="Plein écran"
                            >
                                <Maximize className="w-4 h-4" />
                                <span className="hidden lg:inline">Plein écran</span>
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsFullscreen(false)}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                                title="Quitter le plein écran"
                            >
                                <Minimize className="w-4 h-4" />
                                <span className="hidden lg:inline">Quitter le plein écran</span>
                            </button>
                        )}
                    </div>

                    {/* DROITE */}
                    <div className="flex items-center gap-2">
                        {isOwner && (
                            <div className="flex items-center gap-1 mr-2">
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsPublic(false);
                                        setIsReadOnlyMode(false);
                                        updateFormData({ isPublic: false, isReadOnly: false });
                                    }}
                                    className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors ${!isPublic ? "bg-gray-200 border-gray-400" : "border-gray-300 hover:bg-gray-100"
                                        }`}
                                    title="Privé"
                                >
                                    <Eye className="w-4 h-4" />
                                </button>

                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsPublic(true);
                                        setIsReadOnlyMode(false);
                                        updateFormData({ isPublic: true, isReadOnly: false });
                                    }}
                                    className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors ${isPublic && !isReadOnlyMode ? "bg-blue-100 border-blue-400" : "border-gray-300 hover:bg-gray-100"
                                        }`}
                                    title="Public collaboratif"
                                >
                                    <Globe className="w-4 h-4" />
                                </button>

                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsPublic(true);
                                        setIsReadOnlyMode(true);
                                        updateFormData({ isPublic: true, isReadOnly: true });
                                    }}
                                    className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors ${isPublic && isReadOnlyMode ? "bg-amber-100 border-amber-400" : "border-gray-300 hover:bg-gray-100"
                                        }`}
                                    title="Public lecture seule"
                                >
                                    <Lock className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={async () => {
                                if (isCloud && cloudId && sessionIdRef.current) {
                                    await pocketbaseService.deletePlanSession(sessionIdRef.current);
                                }
                                onCancel();
                            }}
                            className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-4 h-4" />
                            <span className="leading-none">Quitter</span>
                        </button>

                        {!isReadOnly && (
                            <button
                                type="submit"
                                className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Save className="w-4 h-4" />
                                <span className="leading-none">Enregistrer</span>
                            </button>
                        )}
                    </div>
                </div>
            </form>

            {showCustomDialog && (
                <CustomElementDialog
                    onConfirm={handleCreateCustomElement}
                    onCancel={() => {
                        setShowCustomDialog(false);
                        setActiveElementType(null);
                    }}
                />
            )}

            {showPDFPreview && (
                <PDFPreviewDialog
                    plan={planForCanvas}
                    previewImage={previewImage}
                    onClose={() => setShowPDFPreview(false)}
                    onConfirmExport={handleConfirmExportPDF}
                />
            )}
        </div>
    );
}